import { inject, Injectable } from '@angular/core';
import { AuthStore } from './auth.store';
import { AuthCredential, AuthUIState } from './auth.state';
import { AuthQuery } from './auth.query';
import { AuthSession, fetchAuthSession, fetchUserAttributes, signOut, SignOutInput } from '@aws-amplify/auth';
import { take } from 'rxjs/operators';
import { getRoles } from '../../helper/retrieve-roles.helper';
import { Router } from '@angular/router';
import { CustomerStoreService } from '../customer';

@Injectable({ providedIn: 'root' })
export class AuthStoreService {
  private store = inject(AuthStore);
  private query = inject(AuthQuery);
  private customerStoreService = inject(CustomerStoreService);

  /**
   * Updates the auth state
   */
  updateAuthState(session: AuthSession): void {
    if (!session || !session.tokens) {
      this.logout();
      return;
    }
    this.store.update({
      idToken: session.tokens.idToken.toString(),
      accessToken: session.tokens.accessToken.toString(),
      roles: getRoles(session),
    });
  }

  /**
   * Updates the UI state for auth store
   */
  updateUIState(uiState: Partial<AuthUIState>): void {
    this.store.update((state) => ({
      ...state,
      ui: {
        ...state.ui,
        ...uiState,
      },
    }));
  }

  updateUserCredentials(userCredentials: Partial<AuthCredential>): void {
    this.store.update((state) => ({
      ...state,
      ...userCredentials,
    }));
  }

  isLoggedIn(): Promise<boolean> {
    return this.getAuthQuery().loggedIn$.pipe(take(1)).toPromise();
  }

  setRedirectTo(path: string): void {
    this.store.update({ redirectTo: path });
  }

  /**
   * Retrieves the AuthStoreQuery instance
   */
  getAuthQuery(): AuthQuery {
    return this.query;
  }

  resetState(): void {
    this.store.reset();
  }

  getCurrentSession() {
    return fetchAuthSession();
  }

  async logout(opts?: SignOutInput): Promise<void> {
    localStorage.removeItem('AkitaStores');
    await signOut(opts);
    location.href = location.origin;
  }

  async updateSessionState() {
    const { tokens } = await fetchAuthSession({ forceRefresh: true });

    this.updateAuthState({
      tokens: {
        idToken: tokens?.idToken,
        accessToken: tokens?.accessToken,
      },
    });
    const attributes = await fetchUserAttributes();

    this.updateUIState({ userLanguage: attributes.locale });
    return attributes;
  }

  async redirectAfterLogin(router: Router, invitationKey?: string): Promise<void> {
    const redirectPath = this.query.getRedirectPath();
    if (redirectPath) {
      this.setRedirectTo(undefined);
      await router.navigateByUrl(redirectPath);
      return;
    }

    const customer = await this.customerStoreService.initializeCustomerInfo().toPromise();

    if (invitationKey) {
      await router.navigate(['/profile', 'accept-invite', invitationKey]);
    } else {
      switch (customer.registrationStep) {
        case 'COMPANY':
          await router.navigate(['/profile', 'company-data']);
          break;
        case 'PAYMENT':
          await router.navigate(['/profile', 'payment']);
          break;
        default:
          await router.navigate(['/dashboard', 'bookings']);
          break;
      }
    }
  }
}
